// Port of ios/Sources/Models/Acknowledgement.swift + the tally in AcknowledgementSheet.swift (C# twin
// windows/src/SatoriChat.Core/Logic/Acknowledgements.cs, PARITY P346).
//
// An acknowledgement form's live state (`com.satoritech.acknowledgements`, GET /api/v1/forms/{postId}):
// who was asked, the options, how many chose each, what THIS reader answered, and — only for someone
// who may manage it — who answered what. The server decides `can_manage`; it is never inferred here.
// Decoding is lenient where Swift's is (counts / responses / current answer optional), strict on the form.

export interface AcknowledgementOption {
  id: string;
  label: string;
}

export interface AcknowledgementForm {
  postId: string;
  title: string;
  message: string;
  options: AcknowledgementOption[];
  ownerUsername: string | null;
}

export interface AcknowledgementResponse {
  userId: string;
  username: string;
  optionId: string;
  optionLabel: string;
  acknowledgedAt: number;
}

/** One tally line: the option, how many chose it, its share of all answers, and whether it is the reader's. */
export interface AcknowledgementTally {
  option: AcknowledgementOption;
  count: number;
  fraction: number;
  isMine: boolean;
}

export interface AcknowledgementView {
  form: AcknowledgementForm;
  counts: Record<string, number>;
  /** Empty unless the reader may manage the form — the server decides, not the client. */
  responses: AcknowledgementResponse[];
  currentUserResponse: AcknowledgementResponse | null;
  canManage: boolean;
}

export const pluginId = 'com.satoritech.acknowledgements';

function formPath(postId: string): string {
  return `/plugins/${pluginId}/api/v1/forms/${encodeURIComponent(postId)}`;
}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function response(v: unknown): AcknowledgementResponse | null {
  if (!isObject(v)) return null;
  const userId = str(v.user_id);
  const username = str(v.username);
  const optionId = str(v.option_id);
  const optionLabel = str(v.option_label);
  if (userId == null || username == null || optionId == null || optionLabel == null) return null;
  const at = typeof v.acknowledged_at === 'number' ? Math.trunc(v.acknowledged_at) : 0;
  return { userId, username, optionId, optionLabel, acknowledgedAt: at };
}

/** The plugin's answer, or null when the post is not a form / the body is unreadable. */
function decode(json: unknown): AcknowledgementView | null {
  let root: unknown = json;
  if (typeof json === 'string') {
    try {
      root = JSON.parse(json);
    } catch {
      return null;
    }
  }
  if (!isObject(root) || !isObject(root.form)) return null;
  const f = root.form;
  const postId = str(f.post_id);
  const title = str(f.title);
  if (postId == null || title == null || !Array.isArray(f.options)) return null;
  const options: AcknowledgementOption[] = [];
  for (const o of f.options) {
    if (!isObject(o) || str(o.id) == null || str(o.label) == null) return null;
    options.push({ id: o.id as string, label: o.label as string });
  }
  const form: AcknowledgementForm = { postId, title, message: str(f.message) ?? '', options, ownerUsername: str(f.owner_username) };

  // Swift decodes [String: Int] all-or-nothing: one non-integer value drops the whole map.
  let counts: Record<string, number> = {};
  if (isObject(root.counts)) {
    for (const [k, v] of Object.entries(root.counts)) {
      if (typeof v !== 'number' || !Number.isInteger(v)) {
        counts = {};
        break;
      }
      counts[k] = v;
    }
  }
  // Same for the responses: one unreadable entry drops the list.
  let responses: AcknowledgementResponse[] = [];
  if (Array.isArray(root.responses)) {
    for (const r of root.responses) {
      const parsed = response(r);
      if (!parsed) {
        responses = [];
        break;
      }
      responses.push(parsed);
    }
  }
  return { form, counts, responses, currentUserResponse: response(root.current_user_response), canManage: root.can_manage === true };
}

function total(view: AcknowledgementView): number {
  return Object.values(view.counts).reduce((a, b) => a + b, 0);
}

/** The tally first: the sender's question is "how many still owe me an answer". */
function tally(view: AcknowledgementView): AcknowledgementTally[] {
  const sum = total(view);
  return view.form.options.map((option) => {
    const count = view.counts[option.id] ?? 0;
    return { option, count, fraction: sum > 0 ? count / sum : 0, isMine: view.currentUserResponse?.optionId === option.id };
  });
}

/** The names list exists only for a manager, and only when someone answered. */
function showsResponses(view: AcknowledgementView): boolean {
  return view.canManage && view.responses.length > 0;
}

export const Acknowledgements = { pluginId, formPath, decode, total, tally, showsResponses } as const;
